import React, { useEffect, useState } from 'react'
import { Button, HollowButton } from '../../components/Button';
import { Container, ColContainer, CardContainer } from '../../components/Container';
import { FlashMessage } from '../../components/FlashMessage';
import ReactModal from 'react-modal';
import { H1, H2, Byline, H4 } from '../../components/Typography';
import { Helmet } from 'react-helmet';
import { Left, Right } from '../../components/Header';
import { Loading, LoadingError } from '../../components/LoadingFork';
import { css } from 'glamor';
import { useQuery, useMutation } from "@apollo/client"
import { withInstallation } from '../../components/InstallationContext';
import {
    ticketById,
    mutationDeleteTicket,
    mutationAddComment,
    queryGetCommentsByTicket,
    mutationResolveTicket,
    mutationReadStatusUserTicket
} from './queries'
import authManager from "../../lib/authManager";
import { fmtDate } from '../../lib/misc'
import { orderBy } from 'lodash'
import { StatusPill } from '../../components/StatusPill';
import { Box } from '@material-ui/core';
import Avatar from '@material-ui/core/Avatar';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import SendIcon from '@material-ui/icons/Send';
import DeleteIcon from "@material-ui/icons/Delete";
import { gray10, gray30, gray40, gray50, gray60, gray80, white, flavours } from '../../theme';

function Header(props) {
    return <div
        className={css({
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'stretch',
        })}
        {...props}
    />
}


const userColorPalette = [
    { bg: '#E8F5E9', border: '#C8E6C9', avatar: '#4CAF50' },
    { bg: '#FFF3E0', border: '#FFE0B2', avatar: '#FF9800' },
    { bg: '#F3E5F5', border: '#E1BEE7', avatar: '#9C27B0' },
    { bg: '#E0F7FA', border: '#B2EBF2', avatar: '#00BCD4' },
    { bg: '#FBE9E7', border: '#FFCCBC', avatar: '#FF5722' },
    { bg: '#E8EAF6', border: '#C5CAE9', avatar: '#3F51B5' },
    { bg: '#F1F8E9', border: '#DCEDC8', avatar: '#8BC34A' },
    { bg: '#FCE4EC', border: '#F8BBD0', avatar: '#E91E63' },
]

export function supportUserInitials(name) {
    if (!name) return '?'
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
}

export function supportSenderName(item) {
    return item.createdBy.isRootAdmin ? "Drivo Team" : item.createdBy.name
}

export function buildUserColorMap(comments) {
    const colorMap = {}
    let colorIndex = 0
    comments?.forEach((item) => {
        const uid = item.createdById
        if (uid && !(uid in colorMap)) {
            colorMap[uid] = userColorPalette[colorIndex % userColorPalette.length]
            colorIndex++
        }
    })
    return colorMap
}

function TicketDetailsForm({ installation, onCommentAdd, ticket, ticketComments }) {

    const [comment, setComment] = useState('')
    const currentUserId = authManager.userId

    const getInitials = supportUserInitials

    const getSenderName = supportSenderName

    const userColorMap = buildUserColorMap(ticketComments)
    let lastDate = null

    return <div>
        {/* Messages area */}
        <div className={css({ overflowY: 'auto', maxHeight: 570, paddingBottom: 20, height: 600, backgroundColor: '#fdfdfd', scrollbarWidth: 'thin', paddingRight: '30px' })}>
            {(!ticketComments || ticketComments.length === 0) && (
                <div className={css({ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 200 })}>
                    <Typography variant="body1" style={{ color: gray50 }}>No comments</Typography>
                </div>
            )}
            {ticketComments?.map((item) => {
                const isCurrentUser = item.createdById === currentUserId
                const senderName = getSenderName(item)
                const initials = getInitials(senderName)
                const dateLabel = fmtDate(item.createdAt, installation.timeZoneName, 'MMMM dd, yyyy')
                const timeLabel = fmtDate(item.createdAt, installation.timeZoneName, 'h:mm a')
                const showDateSep = dateLabel !== lastDate
                lastDate = dateLabel
                const userColor = userColorMap[item.createdById] || userColorPalette[0]

                return (
                    <div key={item.id}>
                        {/* Date separator */}
                        {showDateSep && (
                            <div className={css({ display: 'flex', justifyContent: 'center', margin: '20px 0' })}>
                                <Chip label={dateLabel} size="small" style={{ backgroundColor: gray10, color: gray60, fontSize: 13 }} />
                            </div>
                        )}

                        {isCurrentUser ? (
                            /* Right-aligned message (current logged-in user) */
                            <div className={css({ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', marginBottom: 20 })}>
                                <div className={css({ maxWidth: '75%' })}>
                                    <div className={css({
                                        background: userColor.bg,
                                        border: `1px solid ${userColor.border}`,
                                        borderRadius: '16px 4px 16px 16px',
                                        padding: '12px 16px',
                                    })}>
                                        <Typography variant="body2" style={{ lineHeight: 1.6, color: gray80, whiteSpace: 'pre-wrap' }}>{item.comment}</Typography>
                                    </div>
                                    <Typography variant="caption" style={{ display: 'block', marginTop: 4, color: gray50, textAlign: 'right' }}>{senderName} &bull; {timeLabel}</Typography>
                                </div>
                                <Avatar style={{
                                    width: 36, height: 36, fontSize: 13, fontWeight: 'bold',
                                    backgroundColor: userColor.avatar, color: white,
                                    flexShrink: 0, marginLeft: 10, marginTop: 2,
                                }}>{initials}</Avatar>
                            </div>
                        ) : (
                            /* Left-aligned message (other users - each with unique color) */
                            <div className={css({ display: 'flex', alignItems: 'flex-start', marginBottom: 20, maxWidth: '75%' })}>
                                <Avatar style={{
                                    width: 36, height: 36, fontSize: 13, fontWeight: 'bold',
                                    backgroundColor: userColor.avatar, color: white,
                                    flexShrink: 0, marginRight: 10, marginTop: 2,
                                }}>{initials}</Avatar>
                                <div>
                                    <div className={css({
                                        background: userColor.bg,
                                        border: `1px solid ${userColor.border}`,
                                        borderRadius: '4px 16px 16px 16px',
                                        padding: '12px 16px',
                                    })}>
                                        <Typography variant="body2" style={{ lineHeight: 1.6, color: gray80, whiteSpace: 'pre-wrap' }}>{item.comment}</Typography>
                                    </div>
                                    <Typography variant="caption" style={{ display: 'block', marginTop: 4, color: gray50 }}>{senderName} &bull; {timeLabel}</Typography>
                                </div>
                            </div>
                        )}
                    </div>
                )
            })}
        </div>

        {/* Reply input area */}
        <div className={css({ borderTop: `1px solid ${gray10}`, paddingTop: 16, paddingBottom: 4 })}>
            <div className={css({
                display: 'flex', alignItems: 'flex-end',
                border: `1px solid ${gray30}`, borderRadius: 24,
                padding: '6px 6px 6px 16px', background: white,
            })}>
                <textarea
                    disabled={ticket.status === 'RESOLVED'}
                    placeholder="Type your reply here..."
                    value={comment}
                    rows={1}
                    onChange={(e) => {
                        setComment(e.target.value)
                        e.target.style.height = 'auto'
                        e.target.style.height = e.target.scrollHeight + 'px'
                    }}
                    className={css({
                        flex: 1,
                        border: 'none',
                        outline: 'none',
                        fontSize: 14,
                        lineHeight: 1.5,
                        color: gray80,
                        background: 'transparent',
                        resize: 'none',
                        overflow: 'hidden',
                        minHeight: 30,
                        maxHeight: 150,
                        padding: '4px 0',
                        fontFamily: 'inherit',
                        '::placeholder': { color: gray40 },
                        ':disabled': { opacity: 0.5 },
                    }).toString()}
                />
                <IconButton
                    aria-label="Send reply"
                    size="small"
                    disabled={!comment || ticket.status === 'RESOLVED'}
                    onClick={(e) => {
                        if (comment && ticket.status !== 'RESOLVED') {
                            onCommentAdd(comment)
                            setComment('')
                            const textarea = e.currentTarget.parentElement.querySelector('textarea')
                            if (textarea) textarea.style.height = 'auto'
                        }
                    }}
                    style={{
                        width: 36, height: 36,
                        backgroundColor: comment ? String(flavours.primary.highlightColor) : gray10,
                        color: comment ? white : gray40,
                        marginLeft: 4,
                    }}
                >
                    <SendIcon fontSize="small" />
                </IconButton>
            </div>
        </div>
    </div>
}

function TicketDetails({installation, match, location, history}) {
    const {ticketId} = match.params
    const [isModalOpen, setModalOpen] = useState(false)
    const [showSuccessMessage, setSuccessMessage] = useState(false)
    const [showErrorMessage, setErrorMessage] = useState(false)
    const [modalType, setModalType] = useState("")
    const [resolveTicket] = useMutation(mutationResolveTicket)
    const [addComment] = useMutation(mutationAddComment)
    const [deleteTicket] = useMutation(mutationDeleteTicket)

    const {data = {}, loading, error, refetch: refetchTicket} = useQuery(ticketById, {
        variables: {
            ticketId,
            installationId: installation.id,
        }
    })
    const ticket = data.getSupportTicketById


    const [updateReadStatus] = useMutation(mutationReadStatusUserTicket)


    const {data: comments, refetch} = useQuery(queryGetCommentsByTicket, {
        variables: {
            ticketId
        }
    })
    const ticketComments = orderBy(comments?.getAllCommentByTicket, ['createdAt'], ['asc']) || []
    const updateStatus = async () => {
        await updateReadStatus({
            variables: {
                ticketId: ticketId,
                userId: authManager.userId
            }
        })
        return true
    }
    // updateStatus()
    useEffect(() => {
        if (comments && comments?.getAllCommentByTicket) {
            updateStatus().then(r => {
                    return r
                }
            )
        }

    }, [comments])

    if (loading) return (<CardContainer><Loading /></CardContainer>)
    if (error) return (<CardContainer><LoadingError error={error} /></CardContainer>)


    if (!ticket) {
        return (
            <CardContainer>
                <Container spaceAbove="medium" spaceBelow="medium">
                    <H1>Not found</H1>
                    <Button onClick={onCancel}>Back to Tickets</Button>
                </Container>
            </CardContainer>
        )
    }

    async function ticketAction() {
        if (modalType === 'Delete') {
            await deleteTicket({
                variables: {
                    id: ticketId
                }
            })
            history.goBack()
        } else {
            await resolveTicket({
                variables: {
                    id: ticketId
                }
            })
            setModalOpen(false)
            refetchTicket()
        }
    }

    async function onCommentAdd(comment) {
        if (comment.trim() !== '') {
            try {
                await addComment({
                    variables: {
                        userId: authManager.userId,
                        comment: comment.trim(),
                        ticketId
                    }
                })
                setSuccessMessage(true)
                setTimeout(() => {
                    setSuccessMessage(false)
                }, 3000)
                refetch()
            } catch (e) {
                setErrorMessage(true)
                setTimeout(() => setErrorMessage(false), 5000)
                refetchTicket()
            }
        }
    }

    function onCancel() {
        history.goBack()
    }

    return (
        <CardContainer>
            <div>
                <Helmet>
                    <title>{`Ticket details`}</title>
                </Helmet>
                <Container spaceBelow="small">
                    <FlashMessage locationState={location.state} />
                    {showErrorMessage && (
                        <div className={css({
                            backgroundColor: '#FEF2F2',
                            border: '1px solid #FECACA',
                            borderRadius: 6,
                            padding: '12px 16px',
                            marginBottom: 16,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            color: '#DC2626',
                            fontSize: 14,
                            fontWeight: 500,
                        })}>
                            This ticket has already been resolved. You can no longer send messages.
                        </div>
                    )}
                    <Header>
                        <Left>
                            <div>
                                <div className={css({ display: 'flex', alignItems: 'center', gap: 12 })}>
                                    {ticket.title ? <H2 spaceBelow="none">{ticket.title}</H2> : <H2 spaceBelow="none">{ticket.supportType}</H2>}
                                    {ticket.status === 'RESOLVED' && (
                                        <StatusPill flavour={'secondary'} size={'xsmall'}>&#10003; RESOLVED</StatusPill>
                                    )}
                                </div>
                                {ticket.title && <H4>{ticket.supportType}</H4>}
                                {ticket.text && <Box mb={2} mr={2}>{ticket.text}</Box>}
                                <Byline>
                                    {ticket?.createdBy?.isRootAdmin ? "Drivo Team" : ticket?.createdBy?.name}
                                    , created at{' '}
                                    {fmtDate(ticket.createdAt, installation.timeZoneName)}
                                </Byline>
                                {(ticket.guestName || ticket.licensePlate) && (
                                    <Byline>
                                        {ticket.guestName && <>Guest: {ticket.guestName}</>}
                                        {ticket.guestName && ticket.licensePlate && <> | </>}
                                        {ticket.licensePlate && <>Plate: {ticket.licensePlate}</>}
                                    </Byline>
                                )}
                            </div>
                        </Left>
                        <Right>
                            {ticket.status === 'OPEN' && (
                                <ColContainer>
                                    <HollowButton onClick={() => {
                                        setModalType("Resolve")
                                        setModalOpen(true)
                                    }} flavour={'primary'} size="small" >Resolve</HollowButton>

                                    <HollowButton onClick={() => {
                                        setModalType("Delete")
                                        setModalOpen(true)
                                    }} flavour={'danger'} size="small" title="Delete Ticket"><DeleteIcon /></HollowButton>
                                </ColContainer>
                            )}
                        </Right>

                    </Header>
                    <hr style={{ 'color': '#B1B1B2' }} />
                </Container>

                <div className={css({ padding: '0 15px' })}>
                    <TicketDetailsForm
                        installation={installation}
                        ticket={ticket}
                        ticketComments={ticketComments}
                        onCommentAdd={onCommentAdd}
                    />
                </div>

                <ReactModal
                    isOpen={isModalOpen}
                    onRequestClose={() => (setModalOpen(false))}
                    className={css({
                        display: 'block',
                        background: 'white',
                        margin: 10,
                        padding: 20,
                        borderRadius: 6,
                        outline: 'none',
                        minWidth: 300,
                        maxWidth: 550,
                    }).toString()}
                    overlayClassName={css({
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'fixed',
                        zIndex: 100000,
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                    }).toString()}
                >
                    <h2 className={css({textAlign: 'left'})}>Are you sure you want to {modalType.toLowerCase()} this support ticket?</h2>

                    <ColContainer>
                        <HollowButton
                            flavour={modalType === "Resolve"? 'primary': 'danger'}
                            className={css({marginTop: 30})}
                            onClick={ticketAction}
                        >
                            {modalType}
                        </HollowButton>
                        <HollowButton
                            flavour={'dark'}
                            className={css({marginTop: 30})}
                            onClick={() => {
                                setModalOpen(false)
                            }}
                        >
                            Cancel
                        </HollowButton>
                    </ColContainer>
                </ReactModal>

            </div>
        </CardContainer>
    )
}

export default withInstallation(TicketDetails)
